// src/features/auth/hooks/useAuth.ts

import { notification } from 'antd';
import { loginUser } from '../api/authApi';
import { useAuthStore } from '../store/authStore';
import { useThemeStore } from '../../../app/store/themeStore';
import type { MenuItem } from '../types/menuTypes';
import type { AxiosError } from 'axios';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router'; // TanStack Router for navigation
import type { LoginFormInputs } from '../types/authTypes';
interface ApiErrorResponse {
  data: null;
  paging: null;
  statusCode: number;
  isError: boolean;
  title: string;
  developerMessage: string;
  message: string;
  messageArgs: null;
}


export function useAuth() {
  const { clearToken, token, clearRefreshToken, setAuthData } = useAuthStore();
  const { setUserTypeTheme } = useThemeStore();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const loginMutation = useMutation({
    mutationFn: (credentials: LoginFormInputs) => loginUser(credentials),
    onSuccess: (data:any) => {
      if (data.isError) {
        notification.error({
          message: 'Login Failed',
          description: data.message,
        });
        return;
      }
      setAuthData(
        data.accessToken,
        data.refreshToken,
        data.menus as MenuItem[],
        data.permissions,
        data.name, // <-- pass name to setAuthData
        data.userType // UserType
      );

      const hasPersistedTheme = !!localStorage.getItem('insurance-theme-storage');
      if (!hasPersistedTheme) {
        setUserTypeTheme(data.userType);
      }

      navigate({ to: '/dashboard' });
    },
    onError: (error: AxiosError<ApiErrorResponse>) => {
      const errorData = error.response?.data;

        if (errorData?.isError) {
          notification.error({
            message: 'Login Failed',
            description: errorData.message,
          });
        } else {
          notification.error({
            message: 'Login Failed',
            description: error.message || 'An unknown error occurred',
          });
        }
      
    },
  });

  const logout = () => {
    clearToken();
    queryClient.clear();
    localStorage.removeItem('insurance-auth-storage');
    navigate({ to: '/auth/login' });
  };

  return {
    token,
    isAuthenticated: !!token,
    login: loginMutation.mutate,
    isLoading: loginMutation.isPending,
    error: loginMutation.error,
    logout,
    clearRefreshToken,
  };
}